// Board, piece and move encoding shared by every variant.
//
// The board is 64 squares, a1 = 0, b1 = 1, … h8 = 63. A square holds 0 (empty) or a piece
// code: `type | colorBit`. Types 1–6 are the standard pieces; higher types are fairy pieces
// registered in pieces.ts. A piece with no colour bit is *neutral* (snails, ducks, walls):
// nobody can capture it and nothing can pass through it, which falls out of the move
// generator for free because captures require an enemy colour bit.
//
// Moves are packed integers so the AI can search without allocating move objects:
//   from (6 bits) | to << 6 | promo << 12 (5 bits) | flags << 17
// For drops, `from` holds the dropped piece type instead of a square.
// Actions (FLAG_ACTION) are variant-defined turns that aren't a piece move: challenging a
// bluff, crowning an heir, a move on a second board. The engine changes nothing for them;
// the variant's afterMove does everything, reading its own meaning into from/to/promo.

export const WHITE = 0;
export const BLACK = 1;
export type Color = 0 | 1;

export const EMPTY = 0;
export const PAWN = 1;
export const KNIGHT = 2;
export const BISHOP = 3;
export const ROOK = 4;
export const QUEEN = 5;
export const KING = 6;

export const TYPE_MASK = 31;
export const WHITE_BIT = 32;
export const BLACK_BIT = 64;
export const COLOR_MASK = WHITE_BIT | BLACK_BIT;

export const CASTLE_WK = 1;
export const CASTLE_WQ = 2;
export const CASTLE_BK = 4;
export const CASTLE_BQ = 8;

export const FLAG_CAPTURE = 1;
export const FLAG_DOUBLE = 2;
export const FLAG_EP = 4;
export const FLAG_CASTLE = 8;
export const FLAG_DROP = 16;
export const FLAG_ACTION = 32;

export const colorBit = (c: Color) => (c === WHITE ? WHITE_BIT : BLACK_BIT);
export const makePiece = (c: Color, type: number) => type | colorBit(c);
export const pieceType = (p: number) => p & TYPE_MASK;
/** WHITE, BLACK, or -1 for empty squares and neutral pieces. */
export const pieceColor = (p: number): Color | -1 =>
  p & WHITE_BIT ? WHITE : p & BLACK_BIT ? BLACK : -1;
export const isNeutral = (p: number) => p !== 0 && (p & COLOR_MASK) === 0;
export const other = (c: Color): Color => (c === WHITE ? BLACK : WHITE);

export const fileOf = (sq: number) => sq & 7;
export const rankOf = (sq: number) => sq >> 3;
export const square = (file: number, rank: number) => rank * 8 + file;
export const onBoard = (file: number, rank: number) => file >= 0 && file < 8 && rank >= 0 && rank < 8;
/** Chebyshev (king-step) distance. */
export function cheb(a: number, b: number): number {
  return Math.max(Math.abs((a & 7) - (b & 7)), Math.abs((a >> 3) - (b >> 3)));
}
export const isLightSquare = (sq: number) => ((sq & 7) + (sq >> 3)) % 2 === 1;

export const encodeMove = (from: number, to: number, promo = 0, flags = 0) =>
  from | (to << 6) | (promo << 12) | (flags << 17);
export const moveFrom = (m: number) => m & 63;
export const moveTo = (m: number) => (m >> 6) & 63;
export const movePromo = (m: number) => (m >> 12) & 31;
export const moveFlags = (m: number) => (m >> 17) & 63;
export const isDrop = (m: number) => (moveFlags(m) & FLAG_DROP) !== 0;
export const isCapture = (m: number) => (moveFlags(m) & FLAG_CAPTURE) !== 0;
export const isAction = (m: number) => (moveFlags(m) & FLAG_ACTION) !== 0;

/** AND-mask applied to castling rights whenever something happens on a square. */
export const CASTLE_MASK = new Int8Array(64).fill(15);
CASTLE_MASK[0] = 15 & ~CASTLE_WQ;
CASTLE_MASK[7] = 15 & ~CASTLE_WK;
CASTLE_MASK[4] = 15 & ~(CASTLE_WK | CASTLE_WQ);
CASTLE_MASK[56] = 15 & ~CASTLE_BQ;
CASTLE_MASK[63] = 15 & ~CASTLE_BK;
CASTLE_MASK[60] = 15 & ~(CASTLE_BK | CASTLE_BQ);

const FILES = 'abcdefgh';

export function squareName(sq: number): string {
  return FILES[sq & 7] + String((sq >> 3) + 1);
}

export function parseSquare(name: string): number {
  if (!/^[a-h][1-8]$/.test(name)) return -1;
  return (name.charCodeAt(1) - 49) * 8 + (name.charCodeAt(0) - 97);
}

/** Deterministic 32-bit PRNG (mulberry32). Variants that need randomness must use a seeded one. */
export function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
